import {ReactNode, useEffect} from 'react';
import {Navigate, useLocation, useSearchParams} from 'react-router';
import {useMutation} from '@tanstack/react-query';
import {Box, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import dayjs from 'dayjs';
import {DefaultButton} from 'src/components/common/DefaultButton';
import {DtxSpacer} from 'src/components/common/DtxSpacer';
import {LoadingSpinner} from 'src/components/common/LoadingSpinner';
import {useAuth} from 'src/auth/context';
import {useQueryWithAccessTokenWithoutContext} from 'src/hooks/TanstackHooks';
import {loginRoute, mcpAuthorizeRequestSearchParamKey} from 'src/routes';
import {
  decideMcpAuthorization,
  getMcpAuthorizationRequest,
} from 'src/services/McpAuthorization/McpAuthorizationService';
import {cacheKeys} from 'src/services/shared/serviceConstants';
import {IMcpAuthorizationRequest} from 'src/types/McpAuthorization';
import {formatDateTime} from 'src/utils/dates';
import {navigateBrowserTo} from 'src/utils/other';

// Consent step of the MCP OAuth flow.
//
// dxn-mcp's /authorize validates the MCP client's request and sends the
// browser here with ?request=<opaque id>. We make sure the user is signed
// in, show what's being asked for, and pass the user's decision back to
// dxn-mcp (via Hasura actions). dxn-mcp answers with the URL to send the
// browser back to the MCP client - for both approve and deny.

// Host of the MCP client's redirect_uri, shown so the user can spot
// a client pretending to be something it isn't.
const redirectHost = (redirectUri: string) => {
  try {
    return new URL(redirectUri).host;
  }
  catch {
    return redirectUri;
  }
};

export const McpAuthorizePage = () => {
  const [searchParams] = useSearchParams();
  const {pathname, search} = useLocation();
  const request = searchParams.get(mcpAuthorizeRequestSearchParamKey) || '';

  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    error: authError,
    getAccessToken,
  } = useAuth();

  const requestQuery = useQueryWithAccessTokenWithoutContext({
    queryKey: [cacheKeys.mcpAuthorizationRequest, request],
    queryFunction: getMcpAuthorizationRequest,
    queryFunctionVariables: {request},
    tanstackOptions: {
      enabled: !!request && isAuthenticated,
      // an unknown or expired request won't get better by retrying
      retry: false,
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: false,
    },
  });

  const decision = useMutation({
    // the token is resolved per call, so a consent page left open
    // past the access token's expiry still presents a fresh one
    mutationFn: async (approve: boolean) => decideMcpAuthorization({
      accessToken: (await getAccessToken()) as string,
      request,
      approve,
    }),
    // Hand the browser back to the MCP client - with a code if approved,
    // with error=access_denied if denied.
    onSuccess: (redirectTo: string) => navigateBrowserTo(redirectTo),
  });

  useEffect(() => {
    if (requestQuery.error) {
      console.error('Error loading MCP authorization request', requestQuery.error);
    }
  }, [requestQuery.error]);

  if (!request) {
    return (
      <McpAuthorizeMessage
        header="Invalid authorization request"
        message="This link is missing its authorization request. Please start again from the application you were connecting."
      />
    );
  }

  if (authError) {
    return (
      <McpAuthorizeMessage
        header="Sign-in failed"
        message="We could not sign you in. Please try again."
      />
    );
  }

  // restoring the session
  if (isAuthLoading) {
    return <LoadingSpinner />;
  }

  // Not signed in - go to the sign-in page and come back to this exact
  // URL, including the request id.
  if (!isAuthenticated) {
    return (
      <Navigate
        to={loginRoute}
        replace
        state={{returnTo: `${pathname}${search}`}}
      />
    );
  }

  const authorizationRequest: IMcpAuthorizationRequest | null | undefined =
    requestQuery.data;

  if (
    requestQuery.isError ||
    (requestQuery.isSuccess && !authorizationRequest)
  ) {
    return (
      <McpAuthorizeMessage
        header="Authorization request expired"
        message="This authorization request is invalid or has expired. Please start again from the application you were connecting."
      />
    );
  }

  if (!authorizationRequest) {
    return <LoadingSpinner />;
  }

  if (decision.isError) {
    return (
      <McpAuthorizeMessage
        header="Authorization failed"
        message="We could not complete this authorization request. It may have expired. Please start again from the application you were connecting."
      />
    );
  }

  return (
    <McpConsent
      authorizationRequest={authorizationRequest}
      // stay disabled after success while the browser navigates away
      isDeciding={decision.isPending || decision.isSuccess}
      onDecide={(approve: boolean) => decision.mutate(approve)}
    />
  );
};

const McpConsent = ({
  authorizationRequest,
  isDeciding,
  onDecide,
}: {
  authorizationRequest: IMcpAuthorizationRequest,
  isDeciding: boolean,
  onDecide: (approve: boolean) => void,
}) => {
  const theme = useTheme();
  const {clientName, redirectUri, expiresAt} = authorizationRequest;

  const paragraphSx = {
    fontSize: theme.typography.h6.fontSize,
    color: theme.palette.text.primary,
  };

  return (
    <Box sx={{maxWidth: '640px', padding: theme.spacing(0, 2)}}>
      <McpAuthorizeHeader>
        Allow {clientName} to access your Detexian data?
      </McpAuthorizeHeader>
      <DtxSpacer space={3} />

      <Typography sx={paragraphSx}>
        {clientName} is asking for <strong>read-only</strong> access to the
        same Detexian data you can see in the portal. It will not be able to
        change anything.
      </Typography>
      <DtxSpacer space={2} />

      <Typography sx={paragraphSx}>
        After you decide, you will be sent back to{' '}
        <strong data-testid="mcp-redirect-host">
          {redirectHost(redirectUri)}
        </strong>
        . Only approve if you recognise this address and started this
        connection yourself.
      </Typography>
      <DtxSpacer space={2} />

      <Typography sx={{...paragraphSx, color: theme.palette.text.secondary}}>
        This request expires {dayjs(expiresAt).fromNow()} (
        {formatDateTime(expiresAt)}).
      </Typography>
      <DtxSpacer space={4} />

      <Box sx={{display: 'flex', justifyContent: 'center', gap: 2}}>
        <DefaultButton
          variant="outlined"
          disabled={isDeciding}
          onClick={() => onDecide(false)}
        >
          Deny
        </DefaultButton>
        <DefaultButton
          variant="contained"
          disabled={isDeciding}
          onClick={() => onDecide(true)}
        >
          Approve
        </DefaultButton>
      </Box>
      <DtxSpacer space={4} />
    </Box>
  );
};

const McpAuthorizeHeader = ({children}: {children: ReactNode}) => {
  const theme = useTheme();
  return (
    <Typography
      variant="h1"
      sx={{
        paddingTop: '0px',
        marginTop: '-70px',
        fontSize: theme.typography.h4.fontSize,
        fontWeight: '900',
        textAlign: 'center',
      }}
    >
      {children}
    </Typography>
  );
};

// error / invalid-request states - no buttons
const McpAuthorizeMessage = ({
  header,
  message,
}: {
  header: string,
  message: string,
}) => {
  const theme = useTheme();
  return (
    <Box sx={{maxWidth: '640px', padding: theme.spacing(0, 2)}}>
      <McpAuthorizeHeader>{header}</McpAuthorizeHeader>
      <DtxSpacer space={3} />
      <Typography
        sx={{
          fontSize: theme.typography.h6.fontSize,
          color: theme.palette.text.primary,
          textAlign: 'center',
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};
